import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SkillList from './SkillList';
import AvailableSkills from './AvailableSkills';
import { ToastContainer, toast } from 'react-toastify';
import { calculateSpCost } from '../../utils/calculateSpCost';
import 'react-toastify/dist/ReactToastify.css';
import './CharacterSkills.css';
import './AvailableSkills.css';
import * as utils from './utils';
import * as api from './utils-api';
import { useCharacter } from "./CharacterProvider";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const calculateBodyPoints = (skills) => {
    const toughnessSkill = skills.find(s =>
        Number(s.skillId) === 31 ||
        (s.name && s.name.trim().toLowerCase() === "toughness")
    );
    return toughnessSkill ? 10 + Number(toughnessSkill.ranks) * 5 : 10;
};

const calculateDexArmor = (skills) => {
    const skill = skills.find(s =>
        Number(s.skillId) === 2 ||
        (s.name && s.name.trim().toLowerCase() === "dexterity armor")
    );
    return skill ? 5 + Number(skill.ranks) * 5 : 0;
};

const calculateMaxArmor = (skills) => {
    const skill = skills.find(s =>
        Number(s.skillId) === 28 ||
        (s.name && s.name.trim().toLowerCase() === "armor training")
    );
    return skill ? 5 + Number(skill.ranks) * 5 : 0;
};

const updatePoolPoints = (skills, poolName, availableSkills) => {
    const baseSkill = availableSkills.find(s => s.name && s.name.trim().toLowerCase() === poolName.toLowerCase());
    if (!baseSkill || !baseSkill.skillId) return 0;
    const poolSkill = skills.find(s => s.skillId === baseSkill.skillId);
    return poolSkill ? Number(poolSkill.ranks) * 10 : 0;
};

function shallowEqualSkills(a = [], b = []) {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        const x = a[i], y = b[i];
        if (x.skillId !== y.skillId) return false;
        if (Number(x.ranks || 0) !== Number(y.ranks || 0)) return false;
        if (Number(x.cost || 0) !== Number(y.cost || 0)) return false;
        if (Number(x.baseCostUsed || 0) !== Number(y.baseCostUsed || 0)) return false;
        if (Number(x.incrementCostUsed || 0) !== Number(y.incrementCostUsed || 0)) return false;
    }
    return true;
}
function shallowEqualCharacter(a, b) {
    if (a === b) return true;
    const keys = [
        "bodyPoints","maxArmor","dexArmor",
        "agilityPoints","arcanePoints","naturePoints","spiritPoints",
        "staminaPoints","thaumaturgyPoints",
        "skillPointsSpent","skillPointsUnspent"
    ];
    for (const k of keys) if ((a?.[k] || 0) !== (b?.[k] || 0)) return false;
    return shallowEqualSkills(a?.skills, b?.skills);
}


const CharacterSkills = ({ token, onSelectCharacter }) => {
    const { currentCharacter, setCurrentCharacter } = useCharacter();
    const [skillsets, setSkillsets] = useState([]);
    const [availableSkills, setAvailableSkills] = useState([]);
    const [saving, setSaving] = useState(false);

    const [activeSkillsetTab, setActiveSkillsetTab] = useState(null);
    const [showAddSkillModal, setShowAddSkillModal] = useState(false);

    api.setToken(token);

    useEffect(() => {
        (async function () {
            setSkillsets(await api.fetchSkillsets());
        })();
    }, [token]);

    useEffect(() => {
        (async function () {
            const fetchedSkills = await api.fetchSkills({characterId: currentCharacter?.characterId});
            setAvailableSkills(fetchedSkills);
            if (currentCharacter && fetchedSkills) {
                setCurrentCharacter(prev =>
                    updateCalculatedFields({
                        ...prev,
                        skills: utils.addStaticSkills(prev.skills, fetchedSkills)
                    })
                );
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCharacter?.characterId, token]);

    const skillsetMap = skillsets.reduce((map, s) => {
        map[String(s.skillsetId)] = s.name;
        return map;
    }, { '999': 'Transform' });

    useEffect(() => {
        if (availableSkills.length > 0 && !activeSkillsetTab) {
            const grouped = availableSkills.reduce((groups, skill) => {
                const groupKey = String(skill.skillset);
                if (!groups[groupKey]) groups[groupKey] = [];
                groups[groupKey].push(skill);
                return groups;
            }, {});
            const groupKeys = Object.keys(grouped).sort((a, b) => {
                const nameA = (skillsetMap[a] || "").toUpperCase();
                const nameB = (skillsetMap[b] || "").toUpperCase();
                return nameA.localeCompare(nameB);
            });
            if (groupKeys.length > 0) setActiveSkillsetTab(groupKeys[0]);
        }
    }, [availableSkills, activeSkillsetTab, skillsetMap]);

    // Recompute ONLY when tiering inputs change, and only write if something changed
    useEffect(() => {
        if (!currentCharacter) return;
        setCurrentCharacter(prev => {
            const next = updateCalculatedFields(prev);
            return shallowEqualCharacter(prev, next) ? prev : next;
        });
    }, [currentCharacter?.skillsetPrimary, currentCharacter?.skillsetSecondary, availableSkills]);

    // Helper: Single-purchase skills have only Primary costs (no Secondary/Tertiary defined)
    const isSinglePurchaseSkill = (skillId) => {
        const s = availableSkills.find(as => as.skillId === skillId);
        // If there's no secondaryBaseCost defined, we treat it as single-purchase
        return !!(s && (s.secondaryBaseCost == null || typeof s.secondaryBaseCost === 'undefined'));
    };

    function updateCalculatedFields(character) {
        const skills = character.skills || [];

        // Add computed props per skill for printing
        const skillsWithComputed = skills.map(s => {
            const { baseCost, incrementCost } = getSkillRate(s, availableSkills, character);
            const cost = getSkillCost(s); // uses your existing logic (incl. slotted groups)
            return { ...s, cost, baseCostUsed: baseCost, incrementCostUsed: incrementCost };
        });

        // Reuse the previous array if annotations didn’t actually change
        const skillsUnchanged = shallowEqualSkills(skills, skillsWithComputed);
        const finalSkills = skillsUnchanged ? skills : skillsWithComputed;

        const spent = skillsWithComputed.reduce((acc, skill) => acc + (skill.cost || 0), 0);

        const computedValues = {
            bodyPoints: calculateBodyPoints(skillsWithComputed),
            maxArmor: calculateMaxArmor(skillsWithComputed) || 0,
            dexArmor: calculateDexArmor(skillsWithComputed) || 0,
            agilityPoints: updatePoolPoints(skillsWithComputed, "Agility Points", availableSkills),
            arcanePoints: updatePoolPoints(skillsWithComputed, "Attunement: Arcane", availableSkills),
            naturePoints: updatePoolPoints(skillsWithComputed, "Attunement: Nature", availableSkills),
            spiritPoints: updatePoolPoints(skillsWithComputed, "Attunement: Spirit", availableSkills),
            staminaPoints: updatePoolPoints(skillsWithComputed, "Stamina Points", availableSkills),
            thaumaturgyPoints: updatePoolPoints(skillsWithComputed, "Attunement: Thaumaturgy", availableSkills),
            skillPointsSpent: spent,
            skillPointsUnspent: (character.skillPointsEffective || character.skillPointsTotal || 0) - spent
        };

        //return { ...character, ...computedValues, skills: skillsWithComputed };
        return { ...character, ...computedValues, skills: finalSkills };
    }


    function calculateSlottedSkillsCosts(skills, slottedSkillNames) {
        const filteredAndSorted = skills
            .filter(s => slottedSkillNames.includes((s.name || '').trim().toLowerCase()))
            .sort((a, b) => (b.ranks || 0) - (a.ranks || 0) || (a.skillId - b.skillId));
        return filteredAndSorted.map((s, i) => {
            if (i === 0) return { skillId: s.skillId, cost: calculateSpCost(s.primaryBaseCost, s.primaryIncrementCost, Number(s.ranks)) };
            if (i === 1) return { skillId: s.skillId, cost: calculateSpCost(s.secondaryBaseCost, s.secondaryIncrementCost, Number(s.ranks)) };
            if (i === 2) return { skillId: s.skillId, cost: calculateSpCost(s.tertiaryBaseCost, s.tertiaryIncrementCost, Number(s.ranks)) };
            return { skillId: s.skillId, cost: 0 };
        });
    }
    function getSlottedGroupTotalCost(skills, groupNames) {
        return calculateSlottedSkillsCosts(skills, groupNames).reduce((sum, s) => sum + s.cost, 0);
    }
    function getSlottedSkillCost(skills, groupNames, skillId, ranksOverride) {
        const arr = calculateSlottedSkillsCosts(
            skills.map(s => (s.skillId === skillId && typeof ranksOverride === 'number' ? { ...s, ranks: ranksOverride } : s)),
            groupNames
        );
        const found = arr.find(s => s.skillId === skillId);
        return found ? found.cost : 0;
    }
    const gatheringSkillNames = ["mining", "foraging", "logging"];
    const productionSkillNames = ["smithing", "alchemy", "runecarving"];

    // Which base+increment costs apply to *this* skill for the current character?
    function getSkillRate(skill, availableSkills, character) {
        const baseSkill = availableSkills.find(as => as.skillId === skill.skillId);
        if (!baseSkill) return { baseCost: 0, incrementCost: 0 };

        const normalizedName = (skill.name || baseSkill.name || '').trim().toLowerCase();
        const characterSkills = character?.skills || [];

        const isGather = ["mining", "foraging", "logging"].includes(normalizedName);
        const isProduce = ["smithing", "alchemy", "runecarving"].includes(normalizedName);

        // For slotted groups, assign Primary/Secondary/Tertiary by highest ranks
        if (isGather || isProduce) {
            const groupNames = isGather
                ? ["mining", "foraging", "logging"]
                : ["smithing", "alchemy", "runecarving"];

            const filteredAndSorted = characterSkills
                .filter(s => groupNames.includes((s.name || '').trim().toLowerCase()))
                .sort((a, b) => (b.ranks || 0) - (a.ranks || 0) || (a.skillId - b.skillId));

            const idx = filteredAndSorted.findIndex(s => s.skillId === skill.skillId);
            if (idx === 0) return {
                baseCost: baseSkill.primaryBaseCost ?? 0,
                incrementCost: baseSkill.primaryIncrementCost ?? 0
            };
            if (idx === 1) return {
                baseCost: baseSkill.secondaryBaseCost ?? 0,
                incrementCost: baseSkill.secondaryIncrementCost ?? 0
            };
            if (idx === 2) return {
                baseCost: baseSkill.tertiaryBaseCost ?? 0,
                incrementCost: baseSkill.tertiaryIncrementCost ?? 0
            };
            // not in top three → no cost
            return { baseCost: 0, incrementCost: 0 };
        }

        // Single-purchase skills: use primary tier
        if (baseSkill.secondaryBaseCost == null) {
            return {
                baseCost: baseSkill.primaryBaseCost ?? 0,
                incrementCost: baseSkill.primaryIncrementCost ?? 0
            };
        }

        // Otherwise pick tier by char's primary/secondary
        if (Number(baseSkill.skillset) === Number(character?.skillsetPrimary)) {
            return {
                baseCost: baseSkill.primaryBaseCost ?? 0,
                incrementCost: baseSkill.primaryIncrementCost ?? 0
            };
        } else if (Number(baseSkill.skillset) === Number(character?.skillsetSecondary)) {
            return {
                baseCost: baseSkill.secondaryBaseCost ?? 0,
                incrementCost: baseSkill.secondaryIncrementCost ?? 0
            };
        } else {
            return {
                baseCost: baseSkill.tertiaryBaseCost ?? 0,
                incrementCost: baseSkill.tertiaryIncrementCost ?? 0
            };
        }
    }


    const getSkillCost = (skill, ranksOverride, asTotalDiff = false) => {
        if (!currentCharacter) return 0;
        const baseSkill = availableSkills.find(s => s.skillId === skill.skillId);
        if (!baseSkill) return 0;
        const normalizedName = (skill.name || baseSkill.name || '').trim().toLowerCase();
        const characterSkills = currentCharacter.skills || [];
        if (gatheringSkillNames.includes(normalizedName)) {
            if (asTotalDiff) {
                const totalNow = getSlottedGroupTotalCost(
                    characterSkills.map(s => {
                        const full = availableSkills.find(as => as.skillId === s.skillId);
                        return full ? { ...full, ranks: s.ranks } : null;
                    }).filter(Boolean),
                    gatheringSkillNames
                );
                const totalReduced = getSlottedGroupTotalCost(
                    characterSkills.map(s => {
                        const full = availableSkills.find(as => as.skillId === s.skillId);
                        if (full && s.skillId === skill.skillId) {
                            return { ...full, ranks: Math.max(0, (s.ranks || 1) - 1) };
                        }
                        return full ? { ...full, ranks: s.ranks } : null;
                    }).filter(Boolean),
                    gatheringSkillNames
                );
                return totalNow - totalReduced;
            }
            return getSlottedSkillCost(
                characterSkills.map(s => {
                    const full = availableSkills.find(as => as.skillId === s.skillId);
                    return full ? { ...full, ranks: (s.skillId === skill.skillId && typeof ranksOverride === 'number' ? ranksOverride : s.ranks) } : null;
                }).filter(Boolean),
                gatheringSkillNames,
                skill.skillId
            );
        }
        if (productionSkillNames.includes(normalizedName)) {
            if (asTotalDiff) {
                const totalNow = getSlottedGroupTotalCost(
                    characterSkills.map(s => {
                        const full = availableSkills.find(as => as.skillId === s.skillId);
                        return full ? { ...full, ranks: s.ranks } : null;
                    }).filter(Boolean),
                    productionSkillNames
                );
                const totalReduced = getSlottedGroupTotalCost(
                    characterSkills.map(s => {
                        const full = availableSkills.find(as => as.skillId === s.skillId);
                        if (full && s.skillId === skill.skillId) {
                            return { ...full, ranks: Math.max(0, (s.ranks || 1) - 1) };
                        }
                        return full ? { ...full, ranks: s.ranks } : null;
                    }).filter(Boolean),
                    productionSkillNames
                );
                return totalNow - totalReduced;
            }
            return getSlottedSkillCost(
                characterSkills.map(s => {
                    const full = availableSkills.find(as => as.skillId === s.skillId);
                    return full ? { ...full, ranks: (s.skillId === skill.skillId && typeof ranksOverride === 'number' ? ranksOverride : s.ranks) } : null;
                }).filter(Boolean),
                productionSkillNames,
                skill.skillId
            );
        }
        const rank = typeof ranksOverride === 'number' ? ranksOverride : (skill.ranks ? Number(skill.ranks) : 1);
        let baseCost, incrementCost;
        if (baseSkill.secondaryBaseCost == null) {
            baseCost = baseSkill.primaryBaseCost;
            incrementCost = baseSkill.primaryIncrementCost;
        } else {
            if (Number(baseSkill.skillset) === Number(currentCharacter.skillsetPrimary)) {
                baseCost = baseSkill.primaryBaseCost;
                incrementCost = baseSkill.primaryIncrementCost;
            } else if (Number(baseSkill.skillset) === Number(currentCharacter.skillsetSecondary)) {
                baseCost = baseSkill.secondaryBaseCost;
                incrementCost = baseSkill.secondaryIncrementCost;
            } else {
                baseCost = baseSkill.tertiaryBaseCost;
                incrementCost = baseSkill.tertiaryIncrementCost;
            }
        }
        return calculateSpCost(baseCost, incrementCost, rank);
    };

    const handleIncrementSkillRank = (skillId) => {
        // Disallow adding more than one rank for single-purchase skills
        /*if (isSinglePurchaseSkill(skillId)) {
            const existing = currentCharacter?.skills?.find(s => s.skillId === skillId);
            if (existing && Number(existing.ranks) >= 1) {
                const skill = availableSkills.find(s => s.skillId === skillId);
                toast.error(`${skill.name} can only be purchased once.`);
                return;
            }
        }*/
        setCurrentCharacter(prev => {
            const updatedSkills = prev.skills.map(skill =>
                skill.skillId === skillId ? { ...skill, ranks: skill.ranks + 1 } : skill
            );
            return updateCalculatedFields({
                ...prev,
                skills: updatedSkills
            });
        });
    };

    const handleDecrementSkillRank = (skillId) => {
        console.log(`Decrementing skill ${skillId}`);
        setCurrentCharacter(prev => {
            const skill = prev.skills.find(s => s.skillId === skillId);
            if (!skill) return prev; // Shouldn't happen

            if (skill.ranks <= 1) {
                console.log(`Removing skill ${skill}`);
                // If only 1 rank, remove the skill entirely
                const updatedSkills = prev.skills.filter(s => s.skillId !== skillId);
                return updateCalculatedFields({
                    ...prev,
                    skills: updatedSkills
                });
            } else {
                // Otherwise, decrement
                const updatedSkills = prev.skills.map(s =>
                    s.skillId === skillId ? { ...s, ranks: s.ranks - 1 } : s
                );
                return updateCalculatedFields({
                    ...prev,
                    skills: updatedSkills
                });
            }
        });
    };

    const handleRemoveSkill = (skillId) => {
        setCurrentCharacter(prev => {
            const updatedSkills = prev.skills.filter(s => s.skillId !== skillId);
            return updateCalculatedFields({
                ...prev,
                skills: updatedSkills
            });
        });
    };

    // Add skill (now only by double-click or button, not DnD)
    const handleDropSkill = skill => {
        if (
            skill.name.trim().toLowerCase() === "attunement: thaumaturgy" &&
            Number(currentCharacter.skillsetPrimary) !== Number(skill.skillset)
        ) {
            toast.error('You must select Thaumaturgy as your Primary Skillset to purchase Attunement: Thaumaturgy.');
            return;
        }
        if (currentCharacter.skills.find(s => s.skillId === skill.skillId)) {
            toast.info('Skill already added.');
            return;
        }
        const skillObj = {
            skillId: skill.skillId,
            ranks: 1,
            name: skill.name,
            prereqs: [],
            skillset: Number(skill.skillset) || 0,
        };
        setCurrentCharacter(prev => {
            const updatedCharacterSkills = [...(prev.skills || []), skillObj];
            return updateCalculatedFields({
                ...prev,
                skills: updatedCharacterSkills
            });
        });
        toast.success(`Added ${skill.name}`);
        setShowAddSkillModal(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const updatedCharacter = {
                ...currentCharacter,
                race: currentCharacter.raceId
            };
            setCurrentCharacter(updatedCharacter);
            await axios.put(`${API_BASE_URL}/character/${currentCharacter.characterId}`, updatedCharacter, {
                headers: { Authorization: `Bearer ${token}` },
            });
            await axios.get(`${API_BASE_URL}/character/${currentCharacter.characterId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Save successful!");
        } catch (error) {
            toast.error("Failed to save character");
        } finally {
            setTimeout(() => setSaving(false), 500);
        }
    };

    return (
        <div>
            {currentCharacter && (
                <div key={currentCharacter.characterId} className="character-edit-container fade-in">

                    {/* Mobile Add Skill Button */}
                    <div className="mobile-add-skill-btn-container">
                        <button
                            className="mobile-add-skill-btn"
                            onClick={() => setShowAddSkillModal(true)}
                        >
                            + Add Skill
                        </button>
                    </div>

                    <div className="skills-section">
                        {/* Current Skills Column */}
                        <div className="skills-column current-skills">
                            <div className="section-label">Purchased Skills (Long tap or right click for description)</div>
                            <div className="current-skills-container">
                                {currentCharacter.skills && currentCharacter.skills.length > 0 ? (
                                    <SkillList
                                        availableSkills={availableSkills}
                                        skillsetMap={skillsetMap}
                                        getSkillCost={getSkillCost}
                                        onIncrement={handleIncrementSkillRank}
                                        onDecrement={handleDecrementSkillRank}
                                        onRemove={handleRemoveSkill}
                                    />
                                ) : (
                                    <p>No skills added.</p>
                                )}
                            </div>
                        </div>
                        {/* Available Skills Column (desktop only) */}
                        <div className="skills-column available-skills">
                            <div className="section-label">
                                Available Skills (Click or tap to add; Long press for description)
                            </div>
                            <AvailableSkills
                                availableSkills={availableSkills}
                                skillsetMap={skillsetMap}
                                activeSkillsetTab={activeSkillsetTab}
                                setActiveSkillsetTab={setActiveSkillsetTab}
                                currentCharacter={currentCharacter}
                                getSkillCost={getSkillCost}
                                handleDropSkill={handleDropSkill}
                            />
                        </div>
                    </div>
                    <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary">
                        {saving ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            )}
            <ToastContainer position="bottom-right" autoClose={3000} />

            {/* Mobile Add Skill Modal */}
            {showAddSkillModal && (
                <div className="mobile-skill-modal-overlay" onClick={() => setShowAddSkillModal(false)}>
                    <div
                        className="mobile-skill-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-header">
                            <span>Add a Skill</span>
                            <button
                                className="close-modal-btn"
                                onClick={() => setShowAddSkillModal(false)}
                            >✕</button>
                        </div>
                        <AvailableSkills
                            availableSkills={availableSkills}
                            skillsetMap={skillsetMap}
                            activeSkillsetTab={activeSkillsetTab}
                            setActiveSkillsetTab={setActiveSkillsetTab}
                            currentCharacter={currentCharacter}
                            getSkillCost={getSkillCost}
                            handleDropSkill={handleDropSkill}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CharacterSkills;
